// @flow
/* eslint-disable jsx-a11y/no-onchange */
import React, { Fragment } from 'react';
import { get, kebabCase } from 'lodash';
import classNames from 'classnames';
import { type FieldProps } from 'formik';

import { ICONS } from 'Components/utils/icon/icons';
import { Icon } from 'Components/frameworks/icon/Icon.jsx';
import { getValidationStatus } from 'Components/frameworks/formik/inputsUtils';

import { ImageValidationStatus } from 'Components/frameworks/image-validation-status/ImageValidationStatus';
import { ErrorNote } from './ErrorNote.jsx';
import { Label } from './Label.jsx';

type Props = {
  dataTestId?: string,
  hideErrorMessage: boolean,
  isDisabled?: boolean,
  isRequired?: boolean,
  label?: string,
  onChange?: (SyntheticEvent<>) => void,
  options: Array<{
    isDisabled?: string,
    key?: string,
    name: string,
    value: string,
  }>,
  placeholder?: string,
  ...FieldProps,
};

export const Select = ({
  dataTestId,
  field,
  form,
  hideErrorMessage,
  isDisabled = false,
  isRequired = false,
  label,
  onChange,
  options,
  placeholder,
}: Props) => {
  if (!options) {
    throw new Error("Can't use select input type without options props");
  }
  // Select value defaults to empty string
  const selectedValue = get(form.values, field.name, '');
  const { isDefaultState, isErrored, isValidated } = getValidationStatus(
    { ...field, value: selectedValue },
    form,
    isDisabled,
  );
  const errorNoteId = `error-${kebabCase(field.name)}`;
  const onSelectChange = onChange ? onChange : field.onChange;

  return (
    <Fragment>
      {!!label && (
        <Label htmlFor={field.name} isDisabled={isDisabled} isRequired={isRequired} label={label} />
      )}
      <div className="flex items-center">
        <select
          aria-describedby={errorNoteId}
          autoComplete="off"
          className={classNames(
            'w-full px-3 py-2 text-md sm:text-base leading-normal bg-transparent border rounded-tiny appearance-none outline-none rtl:pr-10',
            {
              'text-gray-darker': selectedValue,
              'text-gray-medium': !selectedValue,
              'border-gray-medium focus:border-blue': isDefaultState,
              'border-emerald rtl:pr-12': isValidated,
              'border-red formik-errored rtl:pr-12': isErrored,
              'border-gray-lighter bg-gray-lighter cursor-not-allowed': isDisabled,
            },
          )}
          data-testid={dataTestId}
          disabled={isDisabled}
          id={field.name}
          {...field}
          onChange={onSelectChange}
        >
          {placeholder && (
            <option value="" disabled>
              {placeholder}
            </option>
          )}
          {options.map(({ isDisabled: isDisabledOption, key, name, value }) => (
            <option key={key || value} value={value} disabled={isDisabledOption}>
              {name}
            </option>
          ))}
        </select>
        <div className="rtl:right-0 flex items-center pointer-events-none -ml-5">
          <Icon name={ICONS.SELECT} width=".625rem" />
        </div>
        {!isDisabled && (
          <ImageValidationStatus
            isErrored={isErrored}
            isValidated={isValidated}
            classNames="-ml-8"
          />
        )}
      </div>
      {!hideErrorMessage && (
        <ErrorNote
          isErrored={isErrored}
          errorMessage={form.errors[field.name]}
          errorNoteId={errorNoteId}
        />
      )}
    </Fragment>
  );
};
